/**
 * Evidence Enrichment Framework — admin/read client.
 *
 * Generic over entity type (career first; skills, universities, industries,
 * certifications, employers, countries, AI tools and learning pathways plug in
 * without redesign). Descriptive content only — nothing here is scored.
 */
import { supabase } from "@/integrations/supabase/client";
import {
  ENRICHMENT_COLUMNS,
  normaliseEnrichment,
  type CareerEnrichment,
} from "@/lib/careerEnrichment";

export type EnrichmentEntityType =
  | "career" | "skill" | "industry" | "university"
  | "company" | "certification" | "country" | "ai_tool" | "learning_pathway";

export type EnrichmentRow = CareerEnrichment;

export interface EnrichmentCandidate {
  entity_type: string;
  entity_id: string;
  entity_name: string;
  career_family: string | null;
  career_cluster: string | null;
  level: string | null;
  reason: string;
  status: string | null;
  grade: string | null;
  promoted: boolean | null;
  review_status: string | null;
  stale: boolean | null;
  fetch_time: string | null;
}

export interface EnrichmentStats {
  entity_type: string;
  total: number;
  queued: number;
  running: number;
  completed: number;
  failed: number;
  rejected: number;
  blocked: number;
  needs_review: number;
  promoted: number;
  stale: number;
  runs_today: number;
  avg_confidence: number | null;
  avg_runtime_ms: number | null;
  avg_cost_usd: number | null;
}

export interface EnrichmentConfig {
  entity_type: string;
  refresh_days: number;
  chunk_size: number;
  max_budget_usd: number;
  paused: boolean;
}

export interface EnrichmentAuditEntry {
  id: number;
  entity_type: string;
  entity_id: string;
  action: string;
  actor: string | null;
  actor_kind: string;
  detail: Record<string, unknown>;
  created_at: string;
}

export interface DomainUse {
  entity_type: string;
  domain: string;
  uses: number;
}

const db = () => supabase as any;

/* ---------------- reads ---------------- */

export async function listEnrichments(
  entityType: EnrichmentEntityType = "career",
  limit = 300,
): Promise<EnrichmentRow[]> {
  const { data, error } = await db()
    .from("content_enrichment")
    .select(ENRICHMENT_COLUMNS)
    .eq("entity_type", entityType)
    .order("fetch_time", { ascending: false, nullsFirst: false })
    .limit(limit);
  if (error) throw new Error(error.message);
  return ((data ?? []) as any[]).map(normaliseEnrichment);
}

export async function listEnrichmentCandidates(
  entityType: EnrichmentEntityType = "career",
  limit = 500,
): Promise<EnrichmentCandidate[]> {
  const { data, error } = await db()
    .from("content_enrichment_candidates")
    .select(
      "entity_type, entity_id, entity_name, career_family, career_cluster, level, reason, status, grade, promoted, review_status, stale, fetch_time",
    )
    .eq("entity_type", entityType)
    .order("entity_name", { ascending: true })
    .limit(limit);
  if (error) throw new Error(error.message);
  return (data ?? []) as EnrichmentCandidate[];
}

export async function loadEnrichmentStats(
  entityType: EnrichmentEntityType = "career",
): Promise<EnrichmentStats | null> {
  const { data, error } = await db()
    .from("content_enrichment_stats")
    .select("*")
    .eq("entity_type", entityType)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return (data ?? null) as EnrichmentStats | null;
}

export async function loadEnrichmentConfig(
  entityType: EnrichmentEntityType = "career",
): Promise<EnrichmentConfig | null> {
  const { data, error } = await db()
    .from("content_enrichment_config")
    .select("entity_type, refresh_days, chunk_size, max_budget_usd, paused")
    .eq("entity_type", entityType)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return (data ?? null) as EnrichmentConfig | null;
}

export async function saveEnrichmentConfig(
  entityType: EnrichmentEntityType,
  patch: Partial<Omit<EnrichmentConfig, "entity_type">>,
): Promise<void> {
  const { error } = await db()
    .from("content_enrichment_config")
    .update({ ...patch, updated_at: new Date().toISOString() })
    .eq("entity_type", entityType);
  if (error) throw new Error(error.message);
}

export async function listTopDomains(
  entityType: EnrichmentEntityType = "career",
  limit = 25,
): Promise<DomainUse[]> {
  const { data, error } = await db()
    .from("content_enrichment_domains")
    .select("entity_type, domain, uses")
    .eq("entity_type", entityType)
    .order("uses", { ascending: false })
    .limit(limit);
  if (error) throw new Error(error.message);
  return (data ?? []) as DomainUse[];
}

export async function listEnrichmentAudit(
  entityType: EnrichmentEntityType = "career",
  limit = 200,
): Promise<EnrichmentAuditEntry[]> {
  const { data, error } = await db()
    .from("content_enrichment_audit")
    .select("id, entity_type, entity_id, action, actor, actor_kind, detail, created_at")
    .eq("entity_type", entityType)
    .order("created_at", { ascending: false })
    .limit(limit);
  if (error) throw new Error(error.message);
  return (data ?? []) as EnrichmentAuditEntry[];
}

/* ---------------- versioning (immutable) ---------------- */

export interface EnrichmentVersion {
  id: number;
  entity_type: string;
  entity_id: string;
  entity_name: string | null;
  version: number;
  payload: Record<string, { value: string | string[]; sources: string[] }>;
  sources: Array<Record<string, unknown>>;
  conflicts: Array<{ axis: string; claims: string[]; sources: string[] }>;
  grade: string;
  confidence: number;
  trust_score: number;
  coverage: number;
  domain_count: number;
  supported_fields: number;
  freshness: string;
  model: string | null;
  prompt_version: string | null;
  hash: string | null;
  promoted: boolean;
  created_at: string;
}

/** Version history is append-only: v1, v2, v3… nothing is ever overwritten. */
export async function listEnrichmentVersions(
  entityType: EnrichmentEntityType,
  entityId: string,
): Promise<EnrichmentVersion[]> {
  const { data, error } = await db()
    .from("content_enrichment_versions")
    .select("*")
    .eq("entity_type", entityType)
    .eq("entity_id", entityId)
    .order("version", { ascending: false });
  if (error) throw new Error(error.message);
  return (data ?? []) as EnrichmentVersion[];
}

export async function listRecentVersions(
  entityType: EnrichmentEntityType = "career",
  limit = 100,
): Promise<EnrichmentVersion[]> {
  const { data, error } = await db()
    .from("content_enrichment_versions")
    .select("*")
    .eq("entity_type", entityType)
    .order("created_at", { ascending: false })
    .limit(limit);
  if (error) throw new Error(error.message);
  return (data ?? []) as EnrichmentVersion[];
}

/* ---------------- domain trust ---------------- */

export interface DomainTrustRule {
  pattern: string;
  tier: string;
  weight: number;
  is_regex: boolean;
  notes: string | null;
  /** Lower runs first: an allowlist entry outranks the generic tier patterns. */
  priority: number;
  /** Admin blocklist: matching sources are discarded before synthesis. */
  blocked: boolean;
}

const TRUST_COLUMNS = "pattern, tier, weight, is_regex, notes, priority, blocked";

const normaliseTrust = (r: any): DomainTrustRule => ({
  pattern: String(r?.pattern ?? ""),
  tier: String(r?.tier ?? "unknown"),
  weight: Number(r?.weight ?? 0),
  is_regex: Boolean(r?.is_regex),
  notes: r?.notes ?? null,
  priority: Number(r?.priority ?? 100),
  blocked: Boolean(r?.blocked),
});

export async function listDomainTrust(): Promise<DomainTrustRule[]> {
  const { data, error } = await db()
    .from("content_enrichment_domain_trust")
    .select(TRUST_COLUMNS)
    .order("priority", { ascending: true })
    .order("weight", { ascending: false });
  if (error) throw new Error(error.message);
  return ((data ?? []) as any[]).map(normaliseTrust);
}

/** Upsert a batch of rules, keyed on the pattern. Admin-only via RLS. */
export async function saveDomainTrust(rules: DomainTrustRule[]): Promise<void> {
  if (!rules.length) return;
  const { data: { user } } = await supabase.auth.getUser();
  const rows = rules.map((r) => ({
    pattern: r.pattern.trim(),
    tier: r.tier.trim() || "unknown",
    weight: Math.min(1, Math.max(0, Number(r.weight))),
    is_regex: Boolean(r.is_regex),
    notes: r.notes,
    priority: Number.isFinite(r.priority) ? Math.round(r.priority) : 100,
    blocked: Boolean(r.blocked),
    updated_at: new Date().toISOString(),
    updated_by: user?.id ?? null,
  }));
  const { error } = await db()
    .from("content_enrichment_domain_trust")
    .upsert(rows, { onConflict: "pattern" });
  if (error) throw new Error(error.message);
}

export async function deleteDomainTrust(pattern: string): Promise<void> {
  const { error } = await db()
    .from("content_enrichment_domain_trust")
    .delete()
    .eq("pattern", pattern);
  if (error) throw new Error(error.message);
}

/* ---------------- trust maths (mirrors the edge function) ---------------- */

/** Unknown domain: neither authority nor blog. Overridable by an allowlist. */
export const DEFAULT_DOMAIN_TRUST = 0.5;

export const orderTrustRules = (rules: DomainTrustRule[]): DomainTrustRule[] =>
  [...rules].sort(
    (a, b) => (a.priority - b.priority) || (b.weight - a.weight),
  );

const hostOf = (url: string): string => {
  try { return new URL(url).hostname.replace(/^www\./, "").toLowerCase(); }
  catch { return String(url ?? "").replace(/^www\./, "").toLowerCase(); }
};

const matchesRule = (r: DomainTrustRule, host: string): boolean => {
  try { return r.is_regex ? new RegExp(r.pattern, "i").test(host) : host.endsWith(r.pattern); }
  catch { return false; }
};

/** First matching rule in priority order wins — same contract as the engine. */
export function domainTrustFor(
  urlOrHost: string,
  rules: DomainTrustRule[],
): { weight: number; tier: string; blocked: boolean; pattern: string | null } {
  const host = hostOf(urlOrHost);
  for (const r of orderTrustRules(rules)) {
    if (!matchesRule(r, host)) continue;
    return {
      weight: r.blocked ? 0 : r.weight,
      tier: r.blocked ? "blocked" : r.tier,
      blocked: r.blocked,
      pattern: r.pattern,
    };
  }
  return { weight: DEFAULT_DOMAIN_TRUST, tier: "unknown", blocked: false, pattern: null };
}

/** Weighted trust with diminishing returns — ten blogs never beat one .gov. */
export function trustScoreFor(urls: string[], rules: DomainTrustRule[]): number {
  const weights = urls.map((u) => domainTrustFor(u, rules).weight).sort((a, b) => b - a);
  if (!weights.length) return 0;
  let num = 0;
  let den = 0;
  weights.forEach((w, i) => {
    const decay = 1 / (i + 1);
    num += w * decay;
    den += decay;
  });
  return Math.round((num / den) * 1000) / 1000;
}

/* ---------------- CSV bulk editing ---------------- */

export const TRUST_CSV_HEADER = "pattern,tier,weight,is_regex,priority,blocked,notes";

export interface TrustCsvParse {
  rules: DomainTrustRule[];
  errors: string[];
}

const splitCsvLine = (line: string): string[] => {
  const out: string[] = [];
  let cur = "";
  let quoted = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (quoted) {
      if (ch === '"' && line[i + 1] === '"') { cur += '"'; i++; }
      else if (ch === '"') quoted = false;
      else cur += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === ",") { out.push(cur); cur = ""; }
    else cur += ch;
  }
  out.push(cur);
  return out.map((c) => c.trim());
};

const truthyCell = (v: string) => /^(1|true|yes|y)$/i.test(v.trim());

/**
 * Parse an admin CSV of trust rules. Header-driven so column order is free;
 * every rejected row is reported rather than silently dropped.
 */
export function parseTrustCsv(text: string): TrustCsvParse {
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  const errors: string[] = [];
  if (!lines.length) return { rules: [], errors: ["The file is empty."] };

  const header = splitCsvLine(lines[0]).map((h) => h.toLowerCase());
  if (!header.includes("pattern")) {
    return { rules: [], errors: [`Missing a "pattern" column. Expected header: ${TRUST_CSV_HEADER}`] };
  }
  const at = (name: string) => header.indexOf(name);
  const rules: DomainTrustRule[] = [];
  const seen = new Set<string>();

  lines.slice(1).forEach((line, i) => {
    const row = splitCsvLine(line);
    const cell = (name: string) => (at(name) >= 0 ? (row[at(name)] ?? "") : "");
    const pattern = cell("pattern").toLowerCase().replace(/^https?:\/\//, "").replace(/^www\./, "");
    const lineNo = i + 2;
    if (!pattern) { errors.push(`Line ${lineNo}: no pattern.`); return; }
    if (seen.has(pattern)) { errors.push(`Line ${lineNo}: "${pattern}" is duplicated.`); return; }

    const blocked = truthyCell(cell("blocked"));
    const rawWeight = cell("weight");
    const weight = blocked && !rawWeight ? 0 : Number(rawWeight);
    if (!Number.isFinite(weight) || weight < 0 || weight > 1) {
      errors.push(`Line ${lineNo}: weight "${rawWeight}" must be a number between 0 and 1.`);
      return;
    }
    const rawPriority = cell("priority");
    const priority = rawPriority ? Number(rawPriority) : 100;
    if (!Number.isFinite(priority)) { errors.push(`Line ${lineNo}: priority "${rawPriority}" is not a number.`); return; }

    seen.add(pattern);
    rules.push({
      pattern,
      tier: cell("tier") || (blocked ? "blocked" : "allowlist"),
      weight,
      is_regex: truthyCell(cell("is_regex")),
      notes: cell("notes") || null,
      priority: Math.round(priority),
      blocked,
    });
  });

  return { rules, errors };
}

/** Merge an uploaded batch over the live rules, keyed on pattern. */
export function mergeTrustRules(
  current: DomainTrustRule[],
  incoming: DomainTrustRule[],
): DomainTrustRule[] {
  const byPattern = new Map(current.map((r) => [r.pattern, r]));
  for (const r of incoming) byPattern.set(r.pattern, r);
  return orderTrustRules(Array.from(byPattern.values()));
}

export interface TrustImpactRow {
  entity_id: string;
  entity_name: string;
  before: number;
  after: number;
  delta: number;
  grade: string;
  promoted: boolean;
  /** Would this row still clear the 0.60 promotion floor? */
  promotableBefore: boolean;
  promotableAfter: boolean;
  blockedSources: string[];
}

export interface TrustImpact {
  rows: TrustImpactRow[];
  avgBefore: number;
  avgAfter: number;
  gained: number;
  lost: number;
  blockedSources: number;
}

const CONFIDENT_TRUST = 0.6;

/**
 * What the proposed rules would do to trust across the enrichments we hold —
 * recomputed from the stored provenance, without spending a penny on research.
 */
export function previewTrustImpact(
  rows: EnrichmentRow[],
  current: DomainTrustRule[],
  proposed: DomainTrustRule[],
): TrustImpact {
  const out: TrustImpactRow[] = [];
  const blocked = new Set<string>();

  for (const r of rows) {
    const urls = (r.sources ?? []).map((s) => s.url).filter(Boolean);
    if (!urls.length) continue;
    const kept = urls.filter((u) => !domainTrustFor(u, proposed).blocked);
    const droppedHosts = urls
      .filter((u) => domainTrustFor(u, proposed).blocked)
      .map((u) => hostOf(u));
    droppedHosts.forEach((h) => blocked.add(h));

    const before = trustScoreFor(urls, current);
    const after = kept.length ? trustScoreFor(kept, proposed) : 0;
    out.push({
      entity_id: r.entity_id,
      entity_name: r.canonical_name,
      before,
      after,
      delta: Math.round((after - before) * 1000) / 1000,
      grade: r.grade,
      promoted: r.promoted,
      promotableBefore: before >= CONFIDENT_TRUST,
      promotableAfter: after >= CONFIDENT_TRUST && kept.length >= 3,
      blockedSources: Array.from(new Set(droppedHosts)),
    });
  }

  const avg = (k: "before" | "after") =>
    out.length ? Math.round((out.reduce((a, r) => a + r[k], 0) / out.length) * 1000) / 1000 : 0;

  return {
    rows: out.sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta)),
    avgBefore: avg("before"),
    avgAfter: avg("after"),
    gained: out.filter((r) => !r.promotableBefore && r.promotableAfter).length,
    lost: out.filter((r) => r.promotableBefore && !r.promotableAfter).length,
    blockedSources: blocked.size,
  };
}

/* ---------------- reviewer notes ---------------- */

/** Reviewer notes are part of the governance record, exported with the role. */
export async function saveReviewNotes(
  entityType: EnrichmentEntityType,
  entityId: string,
  notes: string,
): Promise<void> {
  const { error } = await db()
    .from("content_enrichment")
    .update({ review_notes: notes.slice(0, 4000), updated_at: new Date().toISOString() })
    .eq("entity_type", entityType)
    .eq("entity_id", entityId);
  if (error) throw new Error(error.message);
}

/* ---------------- governance metrics ---------------- */

export interface ReviewMetrics {
  entity_type: string;
  approved: number;
  rejected: number;
  blocked: number;
  pending: number;
  avg_confidence: number | null;
  avg_trust: number | null;
  avg_coverage: number | null;
  avg_review_seconds: number | null;
  false_positives: number;
  false_negatives: number;
  with_conflicts: number;
}

export async function loadReviewMetrics(
  entityType: EnrichmentEntityType = "career",
): Promise<ReviewMetrics | null> {
  const { data, error } = await db()
    .from("content_enrichment_review_metrics")
    .select("*")
    .eq("entity_type", entityType)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return (data ?? null) as ReviewMetrics | null;
}

export interface DailyCost {
  entity_type: string;
  day: string;
  runs: number;
  firecrawl_usd: number;
  ai_usd: number;
  storage_usd: number;
  total_usd: number;
}

export async function listDailyCost(
  entityType: EnrichmentEntityType = "career",
  days = 60,
): Promise<DailyCost[]> {
  const { data, error } = await db()
    .from("content_enrichment_cost_daily")
    .select("*")
    .eq("entity_type", entityType)
    .order("day", { ascending: false })
    .limit(days);
  if (error) throw new Error(error.message);
  return (data ?? []) as DailyCost[];
}

/** Monthly forecast from the trailing 30 days of actual spend. */
export function forecastMonthlyCost(rows: DailyCost[]): {
  last30: number; perDay: number; forecast: number; firecrawl: number; ai: number; storage: number;
} {
  const cutoff = Date.now() - 30 * 864e5;
  const recent = rows.filter((r) => new Date(r.day).getTime() >= cutoff);
  const sum = (k: keyof DailyCost) => recent.reduce((a, r) => a + Number(r[k] ?? 0), 0);
  const last30 = sum("total_usd");
  const activeDays = Math.max(1, recent.length);
  const perDay = last30 / activeDays;
  return {
    last30: Math.round(last30 * 100) / 100,
    perDay: Math.round(perDay * 100) / 100,
    forecast: Math.round(perDay * 30 * 100) / 100,
    firecrawl: Math.round(sum("firecrawl_usd") * 100) / 100,
    ai: Math.round(sum("ai_usd") * 100) / 100,
    storage: Math.round(sum("storage_usd") * 100) / 100,
  };
}

/* ---------------- end-to-end trace ---------------- */

export interface TraceEntry extends EnrichmentAuditEntry {
  entity_name: string;
  current_version: number | null;
  current_grade: string | null;
  current_status: string | null;
  current_promoted: boolean | null;
  current_hash: string | null;
}

/**
 * Candidate selection → research → grade → review → promotion, in one
 * chronological trail for a single entity or the whole entity type.
 */
export async function listEnrichmentTrace(
  entityType: EnrichmentEntityType = "career",
  entityId?: string,
  limit = 300,
): Promise<TraceEntry[]> {
  let q = db()
    .from("content_enrichment_trace")
    .select("*")
    .eq("entity_type", entityType);
  if (entityId) q = q.eq("entity_id", entityId);
  const { data, error } = await q.order("created_at", { ascending: false }).limit(limit);
  if (error) throw new Error(error.message);
  return (data ?? []) as TraceEntry[];
}


/* ---------------- governed review ---------------- */

export type ReviewDecision = "approve" | "reject" | "block";

/**
 * Promotion is governed: only a `confident` grade may go live, and a block is
 * sticky — the pipeline will not re-promote a blocked entity.
 */
export async function reviewEnrichment(
  entityType: EnrichmentEntityType,
  entityId: string,
  decision: ReviewDecision,
): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();

  if (decision === "approve") {
    const { data: row, error: readErr } = await db()
      .from("content_enrichment")
      .select("grade, coverage, conflicts")
      .eq("entity_type", entityType)
      .eq("entity_id", entityId)
      .maybeSingle();
    if (readErr) throw new Error(readErr.message);
    if (row?.grade !== "confident") {
      throw new Error("Only CONFIDENT evidence can be promoted. Re-run the research first.");
    }
    const conflicts = Array.isArray(row?.conflicts) ? row.conflicts : [];
    if (conflicts.length) {
      throw new Error(
        `Sources contradict each other on ${conflicts.map((c: any) => c.axis).join(", ")}. Resolve the contradiction before promoting.`,
      );
    }
    if (Number(row?.coverage ?? 0) < 0.8) {
      throw new Error("Evidence coverage is below 80%. Too much of this copy is not traceable to a source.");
    }
  }


  const patch =
    decision === "approve"
      ? { promoted: true, status: "completed", review_status: "approved", stale: false, reviewed_at: new Date().toISOString() }
      : decision === "reject"
      ? { promoted: false, status: "rejected", review_status: "rejected", reviewed_at: new Date().toISOString() }
      : { promoted: false, status: "blocked", review_status: "blocked", reviewed_at: new Date().toISOString() };


  const { error } = await db()
    .from("content_enrichment")
    .update({ ...patch, reviewer: user?.id ?? null })
    .eq("entity_type", entityType)
    .eq("entity_id", entityId);
  if (error) throw new Error(error.message);
}

/* ---------------- runs ---------------- */

export interface RunEstimate {
  entities: number;
  firecrawl_searches: number;
  estimated_ai_tokens: number;
  estimated_cost_usd: number;
  estimated_runtime_ms: number;
  max_budget_usd: number;
}

export interface EnrichRunResult {
  ok?: boolean;
  processed?: number;
  promoted?: number;
  note?: string;
  estimate?: RunEstimate;
  results?: Array<{
    entity_id: string;
    name?: string;
    grade?: string;
    promoted?: boolean;
    stale?: boolean;
    domains?: number;
    fields?: number;
    confidence?: number;
    error?: string;
  }>;
  error?: string;
}

async function invoke(body: Record<string, unknown>): Promise<EnrichRunResult> {
  const { data, error } = await supabase.functions.invoke("enrich-content", { body });
  if (error) throw new Error(error.message);
  const result = (data ?? {}) as EnrichRunResult;
  if (result.error) throw new Error(result.error);
  return result;
}

/** Cost estimate + candidate list, with no spend. Shown before every batch. */
export async function estimateRun(opts: {
  entityType?: EnrichmentEntityType;
  entityIds?: string[];
  batch?: number;
}): Promise<EnrichRunResult> {
  return invoke({
    entity_type: opts.entityType ?? "career",
    entity_ids: opts.entityIds,
    batch: opts.batch,
    estimate_only: true,
  });
}

export async function runEnrichment(opts: {
  entityType?: EnrichmentEntityType;
  entityIds?: string[];
  batch?: number;
  force?: boolean;
  maxBudgetUsd?: number;
}): Promise<EnrichRunResult> {
  return invoke({
    entity_type: opts.entityType ?? "career",
    entity_ids: opts.entityIds,
    batch: opts.batch,
    force: opts.force,
    max_budget_usd: opts.maxBudgetUsd,
  });
}

/** Queue the whole emerging set in chunks, respecting pause and budget. */
export async function runBatches(opts: {
  entityType?: EnrichmentEntityType;
  entityIds: string[];
  chunkSize: number;
  maxBudgetUsd?: number;
  shouldStop?: () => boolean;
  onChunk?: (done: number, total: number, result: EnrichRunResult) => void;
}): Promise<{ processed: number; promoted: number; cancelled: boolean }> {
  let processed = 0;
  let promoted = 0;
  const total = opts.entityIds.length;
  for (let i = 0; i < total; i += opts.chunkSize) {
    if (opts.shouldStop?.()) return { processed, promoted, cancelled: true };
    const chunk = opts.entityIds.slice(i, i + opts.chunkSize);
    const result = await runEnrichment({
      entityType: opts.entityType,
      entityIds: chunk,
      maxBudgetUsd: opts.maxBudgetUsd,
    });
    processed += result.processed ?? 0;
    promoted += result.promoted ?? 0;
    opts.onChunk?.(Math.min(i + opts.chunkSize, total), total, result);
  }
  return { processed, promoted, cancelled: false };
}
